import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { INK_COLORS, INK_LITERALS, type InkColor, type InkTone } from './ink-colors';

/**
 * The colour swatches, once.
 *
 * Three editors offer text colour — rich text, the math editor, the reactions editor — and each had
 * its own grid: two groups of fifteen at `h-6` in one, the same at a different size in another, a
 * flat run of `h-5 w-5` bordered circles in the third. Same thirty colours, three different looks,
 * for no reason anybody could point at. The palette DATA was already shared; this shares the
 * rendering too, so a change to how a swatch reads happens once.
 *
 * A light or dark swatch is SPLIT: the tone it shows in light mode on one half, the tone it shows in
 * dark mode on the other, lettered L and D. The picker is drawn in the current theme, so a swatch
 * painted from the token alone would show only half the story — and a swatch labelled "Light Red"
 * that renders dark red in dark mode reads as a bug rather than a feature.
 *
 * A `mid` swatch stays SOLID even though four of them now hold two values, because those two are the
 * same colour calibrated to its ground rather than two swatches trading places. Splitting it would
 * announce a change that is not happening.
 *
 * The split looks identical in both themes on purpose: a stable picture can be learned once, where
 * one that reshuffled on every theme switch could not.
 */
export function InkSwatchGrid({ onPick, size = 'md', literals = false, tones, clearLabel, onClear }: {
  /** Receives what should be STORED — a token normally, a literal where the caller needs one. */
  onPick: (value: string) => void;
  /**
   * Which tones to offer. All thirty by default; the FORMULA editors pass `['mid']`.
   *
   * Colour in prose may be decorative, and a decorative colour is allowed to be hard to read. In
   * a formula it is almost always a highlight, which has to be told apart from the body text
   * beside it — a different job, measured separately. Of the thirty, only the ten mids clear 3:1
   * against the body text AND against the page, in both themes, which means they separate by
   * LIGHTNESS and so survive colour blindness. The dark tones read as black text in light mode;
   * the pale ones vanish against pale text in dark mode.
   */
  tones?: InkTone[];
  /** `lg` for phone-sized touch targets. */
  size?: 'md' | 'lg';
  /** Formula editors store a literal hex: their colour lives in a LaTeX source, where a CSS
   * variable would not survive. `remapInkColors` swaps it for the token when rendered. */
  literals?: boolean;
  /** The "Default" / remove-colour control, when the caller has one. Rendered inside the grid so it
   * joins the same arrow-key sequence as the swatches rather than sitting outside it. */
  clearLabel?: string;
  onClear?: () => void;
}) {
  const { t } = useTranslation();
  const tk = t as (k: string) => string;
  const h = size === 'lg' ? 'h-9' : 'h-6';
  const value = (c: InkColor, i: number) => (literals ? INK_LITERALS[i]! : c.value);

  /**
   * Roving tabindex over the whole set, Default included.
   *
   * The rich-text picker had this and the two formula pickers did not — thirty swatches each,
   * reachable only by thirty Tab presses. Putting it in the shared component is what makes
   * "identical" an upgrade for two of the three rather than a downgrade for one.
   */
  /* Filtered with their ORIGINAL index kept: `INK_LITERALS` is parallel to the unfiltered list,
     so dropping to ten and then indexing by position would hand out the wrong hexes. */
  const shown = INK_COLORS
    .map((c, i) => ({ c, i }))
    .filter(({ c }) => !tones || tones.includes(c.tone));
  /* Every swatch is solid when only mids are offered, so the L/D note explains nothing. */
  const anySplit = shown.some(({ c }) => c.tone !== 'mid');

  const boxRef = useRef<HTMLDivElement>(null);
  const [focusIdx, setFocusIdx] = useState(0);
  const total = (onClear ? 1 : 0) + shown.length;
  const focusAt = (i: number) => {
    const next = (i + total) % total;
    setFocusIdx(next);
    boxRef.current?.querySelectorAll<HTMLButtonElement>('[data-ink-btn]')[next]?.focus();
  };
  const onKeyDown = (e: React.KeyboardEvent, i: number) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 5, ArrowUp: -5 }[e.key];
    if (step !== undefined) { e.preventDefault(); focusAt(i + step); return; }
    if (e.key === 'Home') { e.preventDefault(); focusAt(0); }
    else if (e.key === 'End') { e.preventDefault(); focusAt(total - 1); }
  };
  const offset = onClear ? 1 : 0;

  const swatch = (c: InkColor, inkIdx: number, i: number) => {
    /**
     * Solid on tone, NOT on whether the two values happen to match.
     *
     * Four mids now carry a slightly darker value in the light theme, so `onLight === onDark` would
     * be false for them and they would sprout L/D halves — claiming they swap, which is the opposite
     * of what they do. A mid is one colour calibrated to its ground; only light and dark trade places.
     *
     * And a solid mid paints from `c.value`, the token, so CSS resolves whichever of its two values
     * applies. Painting from `onLight` would show the light-theme colour inside a dark-theme picker.
     */
    const solid = c.tone === 'mid';
    return (
      <button
        key={c.value}
        type='button'
        onMouseDown={(e) => e.preventDefault()}
        data-ink-btn=''
        tabIndex={focusIdx === i + offset ? 0 : -1}
        onKeyDown={(e) => onKeyDown(e, i + offset)}
        onClick={() => onPick(value(c, inkIdx))}
        aria-label={tk(c.labelKey)}
        title={tk(c.labelKey)}
        className={`relative w-full overflow-hidden rounded-full transition-transform hover:scale-110 ${h}`}
        style={solid
          ? { backgroundColor: c.value }
          : { backgroundImage: `linear-gradient(135deg, ${c.onLight} 0 50%, ${c.onDark} 50% 100%)` }}
      >
        {/* L and D name the halves. Decorative — the button's own label already says which tone this
            is — so hidden from assistive tech rather than read out twice. White with a dark shadow
            because the half underneath is an arbitrary colour: neither white nor black alone is
            legible on all thirty, and the shadow carries it on the pale ones. */}
        {!solid && (
          <>
            <span aria-hidden='true' className='absolute left-2 top-1/2 -translate-y-1/2 text-[8px] font-bold leading-none text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9)]'>L</span>
            <span aria-hidden='true' className='absolute right-2 top-1/2 -translate-y-1/2 text-[8px] font-bold leading-none text-white [text-shadow:0_0_2px_rgba(0,0,0,0.9)]'>D</span>
          </>
        )}
      </button>
    );
  };

  return (
    <div ref={boxRef} className='flex flex-col gap-1.5'>
      {onClear && (
        <button
          type='button'
          data-ink-btn=''
          tabIndex={focusIdx === 0 ? 0 : -1}
          onMouseDown={(e) => e.preventDefault()}
          onClick={onClear}
          onKeyDown={(e) => onKeyDown(e, 0)}
          aria-label={clearLabel}
          className='flex w-full items-center justify-center rounded-lg border-2 border-dashed border-ws-border py-1.5 text-xs font-medium text-ws-text transition-colors hover:bg-ws-hover'
        >
          {clearLabel}
        </button>
      )}
      {anySplit ? (
        /* Thirty flow DOWN columns of three, so a column is one hue's light, mid and dark. */
        [shown.slice(0, 15), shown.slice(15)].map((group, gi) => (
          <div key={gi} className='grid grid-flow-col grid-cols-5 grid-rows-3 gap-1.5'>
            {group.map(({ c, i }, j) => swatch(c, i, gi * 15 + j))}
          </div>
        ))
      ) : (
        /* One tone per hue flows ACROSS instead: down-the-column would pair unrelated hues. */
        <div className='grid grid-cols-5 gap-1.5'>
          {shown.map(({ c, i }, j) => swatch(c, i, j))}
        </div>
      )}
      {/* Bottom and centred: it explains the grid above it, so it reads after rather than before,
          and nobody has to discover the behaviour by switching themes and being surprised. */}
      {anySplit && (
        <p className='mt-0.5 text-center text-[11px] italic leading-snug text-ws-muted'>
          {tk('blocks.colors.themeNote')}
        </p>
      )}
    </div>
  );
}
